'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Trophy } from 'lucide-react';
import { fullText } from "@/types/general";
import { motion } from "framer-motion";
import { TrendingUp } from 'lucide-react';
import { hasStoredCookieConsent } from '@/app/lib/cookieConsent';
import { KICK_USERNAME } from '@/types/kick';
import KickIcon from '@/app/components/Navbar/KickIcon';

export default function Hero() {
    const [text, setText] = useState('');
    const [loaded, setLoaded] = useState(false);
    const [nearTop, setNearTop] = useState(true);
    // Lazy-init from localStorage so returning users (who already consented and
    // never see the banner) don't get the arrow stuck hidden for a frame while
    // waiting on CookieConsentModal's mount effect to dispatch the "not visible" event.
    const [cookieBannerVisible, setCookieBannerVisible] = useState(() => {
        if (typeof window === 'undefined') return true;
        return !hasStoredCookieConsent();
    });

    useEffect(() => {
        let index = 0;
        const typingInterval = setInterval(() => {
            if (index <= fullText.length) {
                setText(fullText.slice(0, index));
                index++;
            } else {
                clearInterval(typingInterval);
                setTimeout(() => setLoaded(true), 500);
            }
        }, 120);

        return () => clearInterval(typingInterval);
    }, []);

    useEffect(() => {
        // The arrow must stay below the fixed header once scrolled away, so it
        // doesn't visually poke through it while passing under — only track
        // "near top" as the window where it could overlap the cookie banner instead.
        const onScroll = () => setNearTop(window.scrollY < 200);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        const onCookieBanner = (e: Event) => {
            setCookieBannerVisible((e as CustomEvent<{ visible: boolean }>).detail.visible);
        };
        window.addEventListener('sokoloco:cookie-banner', onCookieBanner);
        return () => window.removeEventListener('sokoloco:cookie-banner', onCookieBanner);
    }, []);

    // While the cookie banner is up and we're near the top, the arrow would sit
    // underneath it — fade it out instead of letting it render on top of the banner.
    const overlapsCookieBanner = nearTop && cookieBannerVisible;

    const scrollToCasinos = () => {
        const casinosSection = document.getElementById('casinos');
        const navbar = document.getElementById('site-navbar');
        
        if (casinosSection) {
            const navbarHeight = navbar ? navbar.offsetHeight : 80;
            const elementPosition = casinosSection.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className="flex flex-col items-center justify-center space-y-8 py-10 px-4 relative">
            {/* Logo Glow */}
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="relative group">
                <div className="absolute -inset-3 bg-gradient-to-r from-green-400 via-emerald-500 to-green-600 rounded-full blur-2xl opacity-60 group-hover:opacity-100 transition duration-700"></div>
                <div className="relative w-45 h-45 md:w-70 md:h-70 rounded-full overflow-hidden shadow-2xl shadow-green-500/40">
                    <Image
                        src="/img/logo.webp"
                        width={400}
                        height={200}
                        alt="sokoloco Logo"
                        className="w-full h-full object-contain"
                        draggable="false"
                    />
                </div>
            </motion.div>

            {/* Title */}
            <motion.h1
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="hero-motion-text text-5xl md:text-7xl font-mono font-bold tracking-widest relative"
            >
                {loaded ? (
                    <span className="bg-gradient-to-r from-green-400 via-emerald-300 to-green-500 bg-clip-text text-transparent drop-shadow-[0_0_15px_rgba(16,185,129,0.8)]">
                        {fullText}
                    </span>
                ) : (
                    <span className="bg-gradient-to-r from-green-400 via-emerald-300 to-green-500 bg-clip-text text-transparent drop-shadow-[0_0_15px_rgba(16,185,129,0.8)]">
                        {text.split('').map((char, i) => (
                            <motion.span
                                key={i}
                                className="inline-block"
                                animate={{ y: [0, -5, 0], rotate: [0, 2, -2, 0] }}
                                transition={{ delay: i * 0.05, repeat: Infinity, repeatDelay: 1, duration: 0.6 }}
                            >
                                {char}
                            </motion.span>
                        ))}
                    </span>
                )}
            </motion.h1>

            {/* Subtext */}
            <div className="space-y-2 font-mono text-sm md:text-base text-green-300 text-center">
                <p><span className="inline-block animate-pulse">{'['}</span> LIVE CASINO STREAMS <span className="inline-block animate-pulse">{']'}</span></p>
                <p><span className="inline-block animate-pulse">{'['}</span> HIGH STAKES • BIG WINS <span className="inline-block animate-pulse">{']'}</span></p>
                <p className="flex items-center justify-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-400 animate-bounce" />
                    <span>COMPETE IN LEADERBOARDS</span>
                    <Trophy className="w-5 h-5 text-yellow-400 animate-bounce" />
                </p>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
                <Link
                    href="/leaderboard/stake"
                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 rounded-xl shadow-lg hover:shadow-emerald-500/30 hover:border-green-400 transition-all duration-200 px-8 py-4 font-mono font-bold text-gray-200 relative overflow-hidden">
                    <TrendingUp className="w-5 h-5" />
                    JOIN LEADERBOARD
                </Link>

                <a
                    href={`https://kick.com/${KICK_USERNAME}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 rounded-xl shadow-lg hover:shadow-emerald-500/30 hover:border-green-400 transition-all duration-200 px-8 py-4 font-mono font-bold text-gray-200 relative overflow-hidden">
                    <KickIcon className="h-4 w-5" />
                    WATCH ON KICK
                </a>
            </div>

            {/* Matrix-Style Scroll Down Arrow */}
            <motion.button
                onClick={scrollToCasinos}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.8 }}
                className={`mt-2 group cursor-pointer relative ${overlapsCookieBanner ? 'z-[190]' : ''}`}
                aria-label="Scroll to casinos section"
            >
                <motion.div
                    animate={{ y: [0, 10, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                    className={`relative transition-opacity duration-300 ${overlapsCookieBanner ? 'opacity-0' : 'opacity-100'}`}
                >
                    {/* Glow effect */}
                    <div className="absolute inset-0 blur-xl opacity-50 group-hover:opacity-75 transition-opacity">
                        <svg width="48" height="48" viewBox="0 0 24 24" className="w-12 h-12 md:w-14 md:h-14">
                            <path
                                d="M7 10L12 15L17 10"
                                stroke="#10b981"
                                strokeWidth="2"
                                strokeLinecap="square"
                                fill="none"
                            />
                        </svg>
                    </div>
                    
                    {/* Main arrow */}
                    <svg 
                        width="48" 
                        height="48" 
                        viewBox="0 0 24 24" 
                        className="w-12 h-12 md:w-14 md:h-14 relative z-10"
                    >
                        {/* Multiple layered arrows for matrix effect */}
                        <motion.path
                            d="M7 10L12 15L17 10"
                            stroke="#10b981"
                            strokeWidth="2.5"
                            strokeLinecap="square"
                            fill="none"
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={{ pathLength: 1, opacity: 1 }}
                            transition={{ duration: 1, repeat: Infinity, repeatDelay: 0.5 }}
                        />
                        <motion.path
                            d="M7 6L12 11L17 6"
                            stroke="#22c55e"
                            strokeWidth="2"
                            strokeLinecap="square"
                            fill="none"
                            initial={{ pathLength: 0, opacity: 0.3 }}
                            animate={{ pathLength: 1, opacity: [0.3, 0.7, 0.3] }}
                            transition={{ duration: 1, repeat: Infinity, repeatDelay: 0.5, delay: 0.2 }}
                        />
                        <motion.path
                            d="M7 14L12 19L17 14"
                            stroke="#059669"
                            strokeWidth="2"
                            strokeLinecap="square"
                            fill="none"
                            initial={{ pathLength: 0, opacity: 0.3 }}
                            animate={{ pathLength: 1, opacity: [0.3, 0.7, 0.3] }}
                            transition={{ duration: 1, repeat: Infinity, repeatDelay: 0.5, delay: 0.1 }}
                        />
                    </svg>
                 
                </motion.div>
                
            </motion.button>
        </div>
    );
}